/* eslint-disable prefer-spread */
// prefer-spread is disabled because we need the current map context while applying each DiffOperation
import sumBy from 'lodash/sumBy';

import {type DiffOperation, diffStyles, type GeoJSONData} from './diff';

// This function acts like `Map#setStyle` https://docs.mapbox.com/mapbox-gl-js/api/#map#setstyle
// but is faster because it skips some expensive validation logic and uses a custom strategy
// for `setGeoJSONSourceData` operations.
export default function fastSetStyle(
    map: mapboxgl.Map,
    prevStyle: mapboxgl.Style | undefined,
    style: mapboxgl.Style
) {
    // @ts-ignore There is no way to access this value publicly even though it's an error to use
    // certain APIs when it's false.
    const isStyleLoaded = map.style && map.style._loaded;

    if (!prevStyle || !style || !isStyleLoaded) {
        return map.setStyle(style);
    }

    const ops = diffStyles(prevStyle, style) as DiffOperation[];

    for (const op of ops) {
        try {
            if (op.command === 'addLayer') {
                map.addLayer.apply(map, op.args as any);
            } else if (op.command === 'removeLayer') {
                map.removeLayer.apply(map, op.args);
            } else if (op.command === 'setPaintProperty') {
                // @ts-ignore The type definition for Mapbox GL JS does not include options
                map.setPaintProperty(op.args[0], op.args[1], op.args[2], {
                    validate: false,
                });
            } else if (op.command === 'setLayoutProperty') {
                // @ts-ignore The type definition for Mapbox GL JS does not include options
                map.setLayoutProperty(op.args[0], op.args[1], op.args[2], {
                    validate: false,
                });
            } else if (op.command === 'setFilter') {
                map.setFilter(op.args[0], op.args[1], {validate: false});
            } else if (op.command === 'addSource') {
                map.addSource.apply(map, op.args);
            } else if (op.command === 'removeSource') {
                map.removeSource.apply(map, op.args);
            } else if (op.command === 'setLayerZoomRange') {
                map.setLayerZoomRange.apply(map, op.args);
            } else if (op.command === 'setLight') {
                map.setLight(op.args[0], {validate: false});
            } else if (op.command === 'setTerrain') {
                map.setTerrain(op.args[0]);
            } else if (op.command === 'setFog') {
                map.setFog(op.args[0]);
            } else if (op.command === 'setGeoJSONSourceData') {
                queueData(map, op.args[0], op.args[1]);
                flushData(map);
            } else if (
                op.command === 'setCenter' ||
                op.command === 'setZoom' ||
                op.command === 'setBearing' ||
                op.command === 'setPitch' ||
                op.command === 'setTransition'
            ) {
                continue;
            } else {
                return map.setStyle(style, {diff: false});
            }
        } catch (error) {
            if (error instanceof Error)
                console.error(`
            Error while executing ${JSON.stringify(op, undefined, '  ')}\n\n${error.toString()}`);
        }
    }
}

interface DataProfile {
    startTime: number;
    endTime: number;
    skippedCount: number;
}

const globalDataState = new Map<
    mapboxgl.Map,
    Record<
        string,
        {
            profiles: DataProfile[];
            queue: undefined | {data: GeoJSONData; startTime: number; skippedCount: number};
        }
    >
>();

// If Mapbox can't keep up with the rate of `setGeoJSONSourceData` operations, it will not render
// anything new until it catches up. `fushData` and `queueData` work around this behavior to
// improve responsiveness. Data updates are "queued" until the map is ready.

function flushData(map: mapboxgl.Map) {
    const mapDataState = globalDataState.get(map);
    if (!mapDataState) return;

    for (const id in mapDataState) {
        const dataState = mapDataState[id];
        const source = map.getSource(id);

        if (source && source.type === 'geojson' && map.isSourceLoaded(id) && dataState.queue) {
            dataState.profiles.push({
                startTime: dataState.queue.startTime,
                endTime: Date.now(),
                skippedCount: dataState.queue.skippedCount,
            });
            if (DEPLOYMENT !== 'production') {
                analyzeDataProfiles(id, dataState.profiles);
            }
            source.setData(dataState.queue.data);
            dataState.queue = undefined;
        }
    }
}

function queueData(map: mapboxgl.Map, id: string, data: GeoJSONData) {
    if (!globalDataState.get(map)) {
        globalDataState.set(map, {});
        map.on('data', () => flushData(map));
    }
    const mapDataState = globalDataState.get(map)!;

    if (!mapDataState[id]) {
        mapDataState[id] = {profiles: [], queue: undefined};
    }
    const sourceDataState = mapDataState[id];

    if (sourceDataState.queue) {
        sourceDataState.queue.data = data;
        sourceDataState.queue.skippedCount++;
    } else {
        sourceDataState.queue = {data, startTime: Date.now(), skippedCount: 0};
    }
}

// We are constantly monitoring the performance of Mapbox GL `setGeoJSONSourceData` operations.
// If the performance drops below a threshold, we print a message with some statistics.
function analyzeDataProfiles(id: string, allProfiles: DataProfile[]) {
    const DURATION = 2000;
    const MIN_FPS = 5;

    const startTime = Date.now() - DURATION;
    const profiles = allProfiles.filter((profile) => profile.startTime >= startTime);

    const renderFPS = (profiles.length / DURATION) * 1000;
    const updateFPS = (sumBy(profiles, (profile) => profile.skippedCount + 1) / DURATION) * 1000;

    if (updateFPS >= MIN_FPS && renderFPS < MIN_FPS && updateFPS - renderFPS >= 1) {
        console.warn(
            `Mapbox GeoJSON source "${id}" is being updated at ${updateFPS.toFixed(
                1
            )} FPS but can only render at ${renderFPS.toFixed(
                1
            )} FPS. Consider simplifying the data or Mapbox style.`
        );
    }
}
